import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload, Boxes, RefreshCw, Trash2, Info, FileText, FileSpreadsheet } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { API, api, errMsg, downloadFile } from "./api";
import { MaterialMap, TYPE_COLOR } from "./MaterialMap";
import { Viewer3D } from "./Viewer3D";
import { ZoneCard } from "./ZoneCard";
import { BandElevation } from "./BandElevation";

const Lbl = ({ children }) => <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">{children}</span>;
const Sel = ({ value, onChange, options, testid, className = "" }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testid} className={`h-9 border-zinc-800 bg-zinc-900 text-xs ${className}`}><SelectValue /></SelectTrigger>
    <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v} className="text-xs">{l}</SelectItem>)}</SelectContent>
  </Select>
);
const Stat = ({ k, v, testid }) => (
  <div className="rounded-lg border border-zinc-800 bg-[#121215] p-3" data-testid={testid}>
    <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">{k}</div>
    <div className="mt-1 font-[Manrope] text-lg font-bold text-white">{v}</div>
  </div>
);

const UNIT_OPTS = [["auto", "Auto (file)"], ["m", "Metres"], ["mm", "Millimetres"], ["cm", "Centimetres"], ["in", "Inches"], ["ft", "Feet"]];
const UP_OPTS = [["auto", "Auto"], ["Z", "Z up (SketchUp)"], ["Y", "Y up (glTF/OBJ)"]];
const BRAND_OPTS = [["bhoruka", "Curtain wall · Bhoruka"], ["bway", "Curtain wall · BWAY"]];
const VIEWS = [["bands", "Floor bands"], ["material", "Materials"]];

const Settings = ({ s, set, hasSection }) => (
  <div className="grid grid-cols-2 gap-3 md:grid-cols-5" data-testid="model-settings">
    <label className="space-y-1"><Lbl>Units</Lbl><Sel testid="model-units" value={s.units} onChange={(v) => set({ ...s, units: v })} options={UNIT_OPTS} /></label>
    <label className="space-y-1"><Lbl>Up axis</Lbl><Sel testid="model-up" value={s.up} onChange={(v) => set({ ...s, up: v })} options={UP_OPTS} /></label>
    <label className="space-y-1"><Lbl>True north (° from +Y)</Lbl><Input data-testid="model-north" type="number" value={s.north_deg} onChange={(e) => set({ ...s, north_deg: Number(e.target.value) })} className="h-9 border-zinc-800 bg-zinc-900 font-mono text-sm" /></label>
    <label className="space-y-1"><Lbl>Fallback floor height (m)</Lbl><Input data-testid="model-floor-h" type="number" step="0.1" value={s.floor_height_m} onChange={(e) => set({ ...s, floor_height_m: Number(e.target.value) })} className="h-9 border-zinc-800 bg-zinc-900 font-mono text-sm" /></label>
    <label className="flex items-end gap-2 pb-2 text-xs text-zinc-300"><Switch data-testid="model-use-section" checked={s.use_section_levels && hasSection} disabled={!hasSection} onCheckedChange={(c) => set({ ...s, use_section_levels: c })} />Use section levels</label>
  </div>
);

const stop = (e) => e.stopPropagation();
const ZoneTable = ({ zones, pick, setPick, hl, setHl }) => (
  <div className="overflow-x-auto rounded-lg border border-zinc-800" data-testid="model-zones-table">
    <table className="w-full min-w-[760px] text-sm">
      <thead className="bg-[#121215] text-left font-mono text-[10px] uppercase tracking-wider text-zinc-500">
        <tr><th className="p-2"></th><th className="p-2">Level · Orientation</th><th className="p-2">Band (m)</th><th className="p-2">Extent</th><th className="p-2">Area</th><th className="p-2">Glazed</th><th className="p-2">Main material</th><th className="p-2">Create as</th></tr>
      </thead>
      <tbody>
        {zones.map((z) => (
          <tr key={z.id} onClick={() => setHl(hl === z.id ? null : z.id)} title="Click to highlight this band on the 3D preview" data-testid={`model-zone-row-${z.id}`} data-highlighted={hl === z.id}
            className={`cursor-pointer border-t border-zinc-800/70 transition-colors ${hl === z.id ? "bg-sky-500/10 shadow-[inset_3px_0_0_#38bdf8]" : "hover:bg-zinc-800/40"}`}>
            <td className="p-2" onClick={stop}><Checkbox data-testid={`model-zone-check-${z.id}`} disabled={!!z.design_id} checked={!!pick[z.id]} onCheckedChange={(c) => setPick((p) => ({ ...p, [z.id]: c ? (p[z.id] || "bhoruka") : undefined }))} /></td>
            <td className="p-2 text-zinc-200"><span className="mr-2 inline-block h-2.5 w-2.5 rounded-sm align-middle" style={{ background: z.color }} data-testid={`model-zone-color-${z.id}`} />{z.label}</td>
            <td className="p-2 font-mono text-xs text-zinc-400">{z.z0}–{z.z1} ({z.height_m})</td>
            <td className="p-2 font-mono text-xs text-zinc-400">{z.extent_m} m</td>
            <td className="p-2 font-mono text-xs text-zinc-200">{z.area_m2} m²</td>
            <td className="p-2 font-mono text-xs text-sky-300">{z.glazed_m2} m²</td>
            <td className="max-w-[160px] truncate p-2 text-xs text-zinc-400" title={Object.entries(z.materials).map(([k, v]) => `${k}: ${v} m²`).join("\n")}>{z.dominant_material}</td>
            <td className="p-2" onClick={stop}>{z.design_id ? <span className="text-xs text-emerald-400">Design created ✓</span> : (
              <Sel testid={`model-zone-brand-${z.id}`} className="w-48" value={pick[z.id] || "bhoruka"} onChange={(v) => setPick((p) => ({ ...p, [z.id]: v }))} options={BRAND_OPTS} />
            )}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Summary = ({ m }) => {
  const s = m.summary;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="model-summary">
        <Stat testid="stat-dims" k="L × W × H" v={`${s.length_m} × ${s.width_m} × ${s.height_m} m`} />
        <Stat testid="stat-footprint" k="Footprint" v={`${s.footprint_m2} m²`} />
        <Stat testid="stat-facade" k="Facade area" v={`${s.facade_m2} m²`} />
        <Stat testid="stat-glazed" k="Glazed" v={`${s.glazed_m2} m²`} />
        <Stat testid="stat-wwr" k="WWR" v={`${s.wwr_pct}%`} />
        <Stat testid="stat-skylight" k="Skylight" v={`${s.skylight_m2} m²`} />
        <Stat testid="stat-roof" k="Roof (opaque)" v={`${s.roof_m2} m²`} />
        <Stat testid="stat-floors" k="Floors" v={`${s.floors} · ${s.levels_source}`} />
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        <div className="rounded-lg border border-zinc-800 p-3 text-xs" data-testid="model-by-orientation">
          <Lbl>By orientation</Lbl>
          <div className="mt-2 grid grid-cols-4 gap-2 font-mono">{Object.entries(s.by_orientation).map(([k, v]) => <div key={k}><div className="text-zinc-500">{k}</div><div className="text-zinc-200">{v.area_m2} m²</div><div className="text-sky-300">{v.glazed_m2} glazed</div></div>)}</div>
        </div>
        <div className="rounded-lg border border-zinc-800 p-3 text-xs" data-testid="model-by-material">
          <Lbl>{m.summary.material_map_applied ? "By cladding type" : "By material (SketchUp)"}</Lbl>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 font-mono">{Object.entries(s.by_material).slice(0, 12).map(([k, v]) => <span key={k} className="text-zinc-300">{k}: <span className="text-zinc-500">{v} m²</span></span>)}</div>
        </div>
      </div>
      <div className="font-mono text-[10px] text-zinc-500" data-testid="model-info-line">Levels {s.levels_m.join(" · ")} m · {m.info.faces} faces ({s.exterior_faces} exterior) · units {m.info.units} · {m.info.up}-up
        {s.frame_members_m2 ? ` · ${s.frame_members_m2} m² columns/frames excluded` : ""}{m.info.ignored_objects?.length ? ` · ${m.info.ignored_objects.length} stray object group(s) away from the building ignored` : ""}</div>
    </div>
  );
};

export default function ModelTakeoff({ project, setProject, onOpenDesign }) {
  const m = project.model;
  const hasSection = (project.section_data?.levels || []).length >= 2;
  const [s, setS] = useState({ units: "auto", up: "auto", north_deg: 0, floor_height_m: 3.6, use_section_levels: true, ...(m?.settings || {}) });
  const [pick, setPick] = useState({});
  const [skyPick, setSkyPick] = useState({});
  const [busy, setBusy] = useState(null);
  const [view, setView] = useState("bands");
  const [hl, setHl] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const hlZone = m?.zones.find((z) => z.id === hl);
  const prevRef = useRef(null);
  const pickHl = (id) => {
    setHl(id);
    setSkyHl(null);
    if (!id) return;
    setView("bands");
    const r = prevRef.current?.getBoundingClientRect();
    if (r && (r.bottom < 80 || r.top > window.innerHeight - 120)) prevRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
  };
  const usedTypes = [...new Set(Object.values(project.material_map || {}))];
  const [skyHl, setSkyHl] = useState(null);
  const [elevId, setElevId] = useState(null);
  const elevZone = m?.zones.find((z) => z.id === elevId);
  const reload = async () => setProject((await api.get(`/projects/${project.id}`)).data);
  const openDesignById = async (id) => onOpenDesign?.((await api.get(`/designs/${id}`)).data);
  const hlSky = m?.skylights.find((k) => k.id === skyHl);
  const selZ = useMemo(() => new Set((m?.zones || []).map((z, i) => ((pick[z.id] || hl === z.id) ? i : -1)).filter((i) => i >= 0)), [m, pick, hl]);
  const selS = useMemo(() => new Set((m?.skylights || []).map((k, i) => ((skyPick[k.id] || skyHl === k.id) ? i : -1)).filter((i) => i >= 0)), [m, skyPick, skyHl]);
  const toggle = (p, id, add, val) => (add ? { ...p, [id]: p[id] ? undefined : val } : { [id]: p[id] || val });
  const onPick3D = (zi, add) => {
    if (zi === null) { setHl(null); setSkyHl(null); return; }
    const z = zi >= 0 ? m.zones[zi] : null;
    const k = zi <= -10 ? m.skylights[-zi - 10] : null;
    if (!z && !k) return;
    const off = add && (z ? pick[z.id] : skyPick[k.id]);
    if (z) {
      const next = z.design_id ? pick : toggle(pick, z.id, add, "bhoruka");
      setPick(next); setSkyHl(null); if (!add) setSkyPick({});
      setHl(off ? Object.keys(next).find((id) => next[id]) || null : z.id);
    } else {
      const next = k.design_id ? skyPick : toggle(skyPick, k.id, add, true);
      setSkyPick(next); setHl(null); if (!add) setPick({});
      setSkyHl(off ? Object.keys(next).find((id) => next[id]) || null : k.id);
    }
  };
  const cardDesign = () => create(hlZone ? [{ kind: "model_zone", id: hlZone.id, brand: pick[hlZone.id] || "bhoruka", system_type: "curtain_wall" }] : [{ kind: "model_skylight", id: hlSky.id, system_type: "skylight" }]);
  const report = (fmt) => run(fmt, () => downloadFile(`/projects/${project.id}/takeoff/report.${fmt}`, `${project.name.replace(/\W+/g, "_")}_takeoff.${fmt}`));
  const n = useMemo(() => Object.values(pick).filter(Boolean).length + Object.values(skyPick).filter(Boolean).length, [pick, skyPick]);

  const run = async (key, fn) => {
    setBusy(key);
    try { await fn(); } catch (e) { toast.error(errMsg(e, "Failed"), { duration: 9000 }); } finally { setBusy(null); }
  };
  const done = (model) => { setProject((p) => ({ ...p, model })); setPick({}); setSkyPick({}); setHl(null); setSkyHl(null); };
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);
  const waitJob = async (jobId) => {
    const t0 = Date.now();
    for (;;) {
      await new Promise((r) => setTimeout(r, 2500));
      if (!alive.current) return;
      if (Date.now() - t0 > 10 * 60 * 1000) throw { response: { data: { detail: "The analysis did not finish within 10 minutes. The server may have restarted; please retry or export a lighter model." } } };
      setElapsed(Math.round((Date.now() - t0) / 1000));
      const { data: j } = await api.get(`/projects/${project.id}/model/jobs/${jobId}`).catch((e) => (e?.response?.status >= 500 || !e?.response ? { data: { status: "running" } } : Promise.reject(e)));
      if (j.status === "done") return done((await api.get(`/projects/${project.id}`)).data.model);
      if (j.status === "error") throw { response: { data: { detail: j.error } } };
    }
  };
  const settings = () => ({ ...s, use_section_levels: s.use_section_levels && hasSection });
  const upload = (file) => run("upload", async () => {
    const fd = new FormData();
    fd.append("file", file);
    Object.entries({ ...settings(), background: true }).forEach(([k, v]) => fd.append(k, String(v)));
    setElapsed(0);
    await waitJob((await api.post(`/projects/${project.id}/model`, fd, { timeout: 300000 })).data.job_id);
    toast.success("Model measured");
  });
  const reanalyse = () => run("re", async () => {
    setElapsed(0);
    await waitJob((await api.post(`/projects/${project.id}/model/reanalyse?background=true`, settings())).data.job_id);
    toast.success("Re-analysed");
  });
  const remove = () => run("del", async () => { await api.delete(`/projects/${project.id}/model`); setProject({ ...project, model: null }); });
  const pickedItems = () => [
    ...Object.entries(pick).filter(([, b]) => b).map(([id, brand]) => ({ kind: "model_zone", id, brand, system_type: "curtain_wall" })),
    ...Object.entries(skyPick).filter(([, v]) => v).map(([id]) => ({ kind: "model_skylight", id, system_type: "skylight" })),
  ];
  const create = (items = pickedItems()) => run("create", async () => {
    const { data } = await api.post(`/projects/${project.id}/takeoff/designs`, { items });
    setProject((await api.get(`/projects/${project.id}`)).data);
    const ids = new Set(items.map((i) => i.id));
    const drop = (p) => Object.fromEntries(Object.entries(p).filter(([id]) => !ids.has(id)));
    setPick(drop);
    setSkyPick(drop);
    toast.success(`${data.created.length} design(s) created in the Systems tab${data.errors.length ? `, ${data.errors.length} failed` : ""}`);
  });

  return (
    <div className="space-y-6" data-testid="model-takeoff">
      <div className="rounded-lg border border-zinc-800 bg-[#121215] p-4">
        <Settings s={s} set={setS} hasSection={hasSection} />
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <label className="inline-flex cursor-pointer items-center rounded-md bg-sky-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-sky-600" data-testid="model-upload-btn">
            {busy === "upload" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}{m ? "Replace 3D model" : "Upload 3D model"}
            <input data-testid="model-file-input" type="file" accept=".dae,.obj,.zip,.glb,.gltf,.stl,.skp" className="hidden" disabled={!!busy} onChange={(e) => { if (e.target.files[0]) upload(e.target.files[0]); e.target.value = ""; }} />
          </label>
          {m && <Button data-testid="model-reanalyse-btn" variant="outline" disabled={!!busy} onClick={reanalyse} className="border-zinc-700">{busy === "re" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}Re-analyse with settings</Button>}
          <Button data-testid="takeoff-report-pdf" variant="outline" disabled={!!busy} onClick={() => report("pdf")} className="border-zinc-700">{busy === "pdf" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />}Take-off PDF</Button>
          <Button data-testid="takeoff-report-xlsx" variant="outline" disabled={!!busy} onClick={() => report("xlsx")} className="border-zinc-700">{busy === "xlsx" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileSpreadsheet className="mr-2 h-4 w-4" />}Excel</Button>
          {(busy === "upload" || busy === "re") && <span className="flex items-center gap-1.5 font-mono text-xs text-sky-300" data-testid="model-job-progress"><Loader2 className="h-3.5 w-3.5 animate-spin" />Analysing model… {elapsed}s (large models take 20–60 s)</span>}
          {m && <Button data-testid="model-delete-btn" variant="outline" disabled={!!busy} onClick={remove} className="border-zinc-700"><Trash2 className="h-4 w-4" /></Button>}
          <span className="flex items-center gap-1.5 text-xs text-zinc-500"><Info className="h-3.5 w-3.5" />Best: SketchUp File › Export › 3D Model › COLLADA (.dae). Native .skp is read best-effort only.</span>
        </div>
      </div>
      {m && (
        <>
          <div className="grid gap-6 xl:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
            {(m.preview_path || m.mesh_path) && (
              <div className="space-y-2">
                <div className="flex gap-1" data-testid="model-view-toggle">
                  {VIEWS.map(([k, l]) => (
                    <button key={k} data-testid={`model-view-${k}`} onClick={() => { setView(k); if (k === "material") setHl(null); }} className={`rounded-md border px-3 py-1 text-xs transition-colors ${view === k ? "border-sky-500/50 bg-sky-500/10 text-sky-300" : "border-zinc-800 text-zinc-400 hover:text-white"}`}>{l}</button>
                  ))}
                  {hlZone && (
                    <button data-testid="model-highlight-clear" onClick={() => setHl(null)} className="ml-auto rounded-md border border-sky-500/40 bg-sky-500/10 px-3 py-1 text-xs text-sky-300 transition-colors hover:bg-sky-500/20">
                      <span className="mr-1.5 inline-block h-2 w-2 rounded-sm" style={{ background: hlZone.color }} />{hlZone.label} · {hlZone.area_m2} m² ✕
                    </button>
                  )}
                </div>
                <div ref={prevRef}>
                  {m.mesh_path ? <Viewer3D project={project} m={m} mode={view} selZ={selZ} selS={selS} onPick={onPick3D} /> : (
                    <>
                      <img data-testid="model-preview" alt="3D model envelope" src={`${API}/projects/${project.id}/model/preview?mode=${m.bands_path ? view : "material"}${hl && m.proj_path ? `&zone=${hl}` : ""}&v=${encodeURIComponent(m.map_version || m.uploaded_at)}`} className="w-full rounded-lg border border-zinc-800" />
                      <div className="text-xs text-amber-300" data-testid="model-viewer-needs-reanalyse">Re-analyse the model once to enable the interactive 3D viewer.</div>
                    </>
                  )}
                </div>
                <ZoneCard zone={hlZone} sky={hlSky} brand={hlZone && pick[hlZone.id]} setBrand={(v) => setPick((p) => ({ ...p, [hlZone.id]: v }))} onDesign={cardDesign} onElevation={() => setElevId(hlZone.id)} onClose={() => { setHl(null); setSkyHl(null); }} busy={busy === "create"} />
                {hl && !m.proj_path && !m.mesh_path && <div className="text-xs text-amber-300" data-testid="model-highlight-needs-reanalyse">Re-analyse the model once to enable band highlighting.</div>}
                {view === "material" && usedTypes.length > 0 && (
                  <div className="flex flex-wrap gap-x-3 gap-y-1 font-mono text-[10px] text-zinc-400" data-testid="model-material-legend">
                    {usedTypes.map((t) => <span key={t}><span className="mr-1 inline-block h-2 w-2 rounded-sm" style={{ background: TYPE_COLOR[t] }} />{t}</span>)}
                    <span><span className="mr-1 inline-block h-2 w-2 rounded-sm bg-zinc-400" />Unassigned opaque</span><span><span className="mr-1 inline-block h-2 w-2 rounded-sm bg-sky-500" />Unassigned glazed</span>
                  </div>
                )}
                {view === "bands" && m.bands_path && (
                  <div className="flex flex-wrap gap-x-3 gap-y-1 font-mono text-[10px] text-zinc-400" data-testid="model-bands-legend">
                    {m.zones.map((z) => <button key={z.id} data-testid={`model-legend-${z.id}`} onClick={() => pickHl(hl === z.id ? null : z.id)} className={`transition-opacity ${hl && hl !== z.id ? "opacity-40" : ""} hover:text-white`}><span className="mr-1 inline-block h-2 w-2 rounded-sm" style={{ background: z.color }} />{z.label}</button>)}
                    <span><span className="mr-1 inline-block h-2 w-2 rounded-sm bg-cyan-300" />Skylight</span><span><span className="mr-1 inline-block h-2 w-2 rounded-sm bg-zinc-700" />Roof / soffit</span>
                  </div>
                )}
              </div>
            )}
            <Summary m={m} />
          </div>
          {elevZone && <BandElevation key={elevZone.id} project={project} zone={elevZone} onClose={() => setElevId(null)} onDone={reload} onOpenDesign={onOpenDesign && openDesignById} />}
          <MaterialMap project={project} setProject={setProject} />
          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h3 className="font-[Manrope] text-lg font-bold">Facade bands · floor level × orientation</h3>
              <Button data-testid="takeoff-create-btn" disabled={!n || !!busy} onClick={() => create()} className="bg-amber-500 text-black hover:bg-amber-400">
                {busy === "create" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Boxes className="mr-2 h-4 w-4" />}{n ? `Create ${n} design${n === 1 ? "" : "s"}` : "Create designs"}
              </Button>
            </div>
            <p className="text-xs text-zinc-500">Tap a face in the 3D view (or click a row) to select its band; Shift/Ctrl adds more. Design one zone from its card, or tick several and create them together.</p>
            <ZoneTable zones={m.zones} pick={pick} setPick={setPick} hl={hl} setHl={pickHl} />
          </div>
          <div className="space-y-2" data-testid="model-skylights">
            <h3 className="font-[Manrope] text-lg font-bold">Skylights {m.skylights.length ? "" : "· none found"}</h3>
            {m.skylights.map((k) => (
              <label key={k.id} className="flex items-center gap-3 rounded-md border border-zinc-800 p-2 text-sm" data-testid={`model-skylight-${k.id}`}>
                {k.design_id ? <span className="text-xs text-emerald-400">✓</span> : <Checkbox data-testid={`model-skylight-check-${k.id}`} checked={!!skyPick[k.id]} onCheckedChange={(c) => setSkyPick((p) => ({ ...p, [k.id]: c }))} />}
                <span className="text-zinc-200">{k.label}</span>
                <span className="font-mono text-xs text-zinc-400">{k.area_m2} m² · plan {k.plan_length_m} × {k.plan_width_m} m · {k.slope_deg}° · top +{k.z_m} m</span>
              </label>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
